import { useEffect, useState, useCallback } from "react";
import { api, errMsg, today, addDays, fmtDate, formatHours, prettyDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import AddRepairDialog from "@/components/AddRepairDialog";
import CapacityBar from "@/components/CapacityBar";
import WeekView from "@/components/WeekView";
import { ChevronLeft, ChevronRight, Trash2, CheckCircle2, Split, Edit, StickyNote, Download, Plus, LayoutGrid, BookOpen, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { generateAgendaPDF } from "@/lib/pdf";

const VIEW_KEY = "agenda_view_mode";
const COLS_KEY = "agenda_cols";
// Zoom levels: number of day-columns rendered per row.
// Days shown grows accordingly so every column is useful.
const COLS_OPTIONS = [1, 2, 3, 4];
const DAYS_BY_COLS = { 1: 7, 2: 14, 3: 21, 4: 28 };
const GRID_CLASS_BY_COLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export default function Agenda() {
  const [startDate, setStartDate] = useState(new Date());
  const [agenda, setAgenda] = useState({});
  const [capacity, setCapacity] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newDate, setNewDate] = useState(null);  // date to preselect when opening new-repair dialog
  const [newOpen, setNewOpen] = useState(false);
  const [workshopName, setWorkshopName] = useState("");
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || "list"; } catch { return "list"; }
  });
  const [cols, setCols] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(COLS_KEY) || "1", 10);
      return COLS_OPTIONS.includes(v) ? v : 1;
    } catch { return 1; }
  });
  const range = DAYS_BY_COLS[cols];
  const compact = cols > 1;
  const gridClass = GRID_CLASS_BY_COLS[cols];

  useEffect(() => {
    try { localStorage.setItem(VIEW_KEY, view); } catch { /* ignore */ }
  }, [view]);
  useEffect(() => {
    try { localStorage.setItem(COLS_KEY, String(cols)); } catch { /* ignore */ }
  }, [cols]);

  const zoomOut = () => {
    const idx = COLS_OPTIONS.indexOf(cols);
    if (idx < COLS_OPTIONS.length - 1) setCols(COLS_OPTIONS[idx + 1]);
  };
  const zoomIn = () => {
    const idx = COLS_OPTIONS.indexOf(cols);
    if (idx > 0) setCols(COLS_OPTIONS[idx - 1]);
  };

  const load = useCallback(async () => {
    const from = fmtDate(startDate);
    const to = fmtDate(addDays(startDate, range - 1));
    const [agendaRes, capRes, meRes] = await Promise.all([
      api.get(`/agenda?from_date=${from}&to_date=${to}`),
      api.get(`/capacity?from_date=${from}&to_date=${to}`),
      api.get("/workshop/me"),
    ]);
    setAgenda(agendaRes.data);
    setCapacity(capRes.data);
    setWorkshopName(meRes.data.name);
  }, [startDate, range]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta cita?")) return;
    try {
      await api.delete(`/repairs/${id}`);
      toast.success("Cita eliminada");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };
  const handleComplete = async (id) => {
    try {
      await api.put(`/repairs/${id}/complete`);
      toast.success("Marcada como completada");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo completar"));
    }
  };
  const handleEdit = async (id) => {
    try {
      const res = await api.get("/repairs");
      const repair = res.data.find((r) => r.id === id);
      if (!repair) { toast.error("No encontrada"); return; }
      setEditTarget(repair);
      setEditOpen(true);
    } catch (e) {
      toast.error(errMsg(e, "No se pudo abrir"));
    }
  };

  const openNewOnDate = (d) => {
    if (d.is_holiday) { toast.error("Ese día es festivo"); return; }
    if (!d.is_workday) { toast.error("Ese día no es laborable"); return; }
    if (d.total_mechanic_hours <= 0) { toast.error("Sin mecánicos disponibles ese día"); return; }
    if (d.available <= 0) { toast.error("Ese día está lleno. La cita saltará al siguiente día disponible."); }
    setNewDate(d.date);
    setNewOpen(true);
  };

  const downloadPDF = () => {
    if (!capacity) return;
    generateAgendaPDF({
      workshopName,
      title: "Agenda Semanal · Mecánica General",
      days: capacity.days,
      agendaByDate: agenda,
      kind: "general",
    });
    toast.success("PDF generado");
  };

  const days = capacity?.days || [];

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Agenda</h1>
          <p className="text-slate-500 mt-1">{cols === 1 ? "Un día por fila" : `${cols} días por fila`} · {range} días en total.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1" data-testid="zoom-toggle">
            <button
              onClick={zoomOut}
              disabled={cols >= COLS_OPTIONS[COLS_OPTIONS.length - 1]}
              className="h-9 w-9 rounded-md inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              data-testid="zoom-out-btn"
              title="Ver más días por fila"
              aria-label="Ver más días por fila"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="px-2 min-w-[52px] text-center text-sm font-medium text-slate-700 self-center tabular-nums" data-testid="days-range-label">
              {cols}×
            </span>
            <button
              onClick={zoomIn}
              disabled={cols <= COLS_OPTIONS[0]}
              className="h-9 w-9 rounded-md inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              data-testid="zoom-in-btn"
              title="Ver menos días por fila"
              aria-label="Ver menos días por fila"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1" data-testid="view-toggle">
            <button
              onClick={() => setView("list")}
              className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="view-list-btn"
              title="Vista lista"
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="hidden sm:inline">Lista</span>
            </button>
            <button
              onClick={() => setView("week")}
              className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                view === "week" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid="view-week-btn"
              title="Vista agenda tradicional"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Semana</span>
            </button>
          </div>
          <Button variant="outline" onClick={downloadPDF} className="h-12" data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
          <AddRepairDialog onCreated={load} />
        </div>
      </header>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={() => setStartDate((d) => addDays(d, -range))}
          data-testid="agenda-prev"
          className="h-11"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
        </Button>
        <Button
          variant="outline"
          onClick={() => setStartDate(new Date())}
          data-testid="agenda-today"
          className="h-11"
        >
          Hoy
        </Button>
        <Button
          variant="outline"
          onClick={() => setStartDate((d) => addDays(d, range))}
          data-testid="agenda-next"
          className="h-11"
        >
          Siguiente <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <div className={compact ? "space-y-2" : "space-y-4"}>
        {view === "week" ? (
          <WeekView
            days={days}
            agenda={agenda}
            onOpenNew={openNewOnDate}
            onComplete={handleComplete}
            onEdit={handleEdit}
            onDelete={handleDelete}
            cols={cols}
          />
        ) : (
          <div className={`grid ${gridClass} gap-3`} data-testid="agenda-grid">
          {days.map((d) => {
          const items = agenda[d.date] || [];
          const isToday = d.date === today();
          return (
            <Card key={d.date} data-testid={`agenda-day-${d.date}`} className={isToday ? "border-blue-500 border-2" : d.is_holiday ? "border-red-200 bg-red-50" : ""}>
              <CardHeader className={compact ? "pb-2 pt-3" : "pb-3"}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className={compact ? "font-display text-base" : "font-display text-lg"}>
                    {prettyDate(d.date)} {isToday && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded ml-2">Hoy</span>}
                    {d.is_holiday && (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded ml-2" data-testid={`holiday-badge-${d.date}`}>
                        Festivo {d.holiday_label ? `· ${d.holiday_label}` : ""}
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-500">
                      {d.is_holiday ? (
                        <span>No se programa</span>
                      ) : d.is_workday ? (
                        <>
                          <span className="font-semibold text-slate-900">{formatHours(d.used)}</span> / {formatHours(d.schedulable)}
                        </>
                      ) : (
                        <span>No laborable</span>
                      )}
                    </div>
                    {d.is_workday && !d.is_holiday && d.total_mechanic_hours > 0 && (
                      <Button
                        size="sm"
                        onClick={() => openNewOnDate(d)}
                        disabled={d.available <= 0}
                        className={d.available > 0 ? "bg-blue-700 hover:bg-blue-800 h-9" : "bg-slate-300 h-9"}
                        data-testid={`add-cita-${d.date}`}
                        title={d.available > 0 ? "Nueva cita en este día" : "Día lleno"}
                      >
                        <Plus className={`w-4 h-4 ${cols >= 3 ? "" : "mr-1"}`} />
                        {cols < 3 && <span className={cols === 2 ? "hidden sm:inline" : ""}>{d.available > 0 ? "Nueva cita" : "Lleno"}</span>}
                      </Button>
                    )}
                  </div>
                </div>
                {d.is_workday && !d.is_holiday && d.total_mechanic_hours > 0 && (
                  <div className="mt-2">
                    <CapacityBar used={d.used} schedulable={d.schedulable} total={d.total_mechanic_hours} label="" />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="text-sm text-slate-400 italic">{d.is_holiday ? "Día festivo" : "Sin citas"}</div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {items.map((it, idx) => (
                      <li key={`${it.repair_id}-${idx}`} className="py-3" data-testid={`agenda-item-${it.repair_id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                            <div className="flex items-center gap-2 flex-wrap mb-1.5">
                              {it.license_plate && (
                                <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded text-sm">
                                  {it.license_plate}
                                </span>
                              )}
                              {(it.brand || it.model) && (
                                <span className="text-sm text-slate-700 font-medium">{it.brand} {it.model}</span>
                              )}
                              {it.is_split && (
                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded flex items-center gap-1">
                                  <Split className="w-3 h-3" /> Dividida
                                </span>
                              )}
                              {it.status === "completed" && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Completada</span>
                              )}
                            </div>
                            <div className="text-slate-900 font-medium break-words">{it.description}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {it.customer_name && <span>{it.customer_name}</span>}
                              {it.phone && <span className="ml-2">· {it.phone}</span>}
                            </div>
                            {it.notes && (
                              <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded px-2 py-1.5" data-testid={`repair-notes-${it.repair_id}`}>
                                <StickyNote className="w-3.5 h-3.5 mt-0.5 text-blue-600 shrink-0" />
                                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{it.notes}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                            <span className="text-2xl font-display font-bold text-slate-900">{formatHours(it.hours)}</span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-slate-600 hover:bg-slate-100"
                                onClick={() => handleEdit(it.repair_id)}
                                data-testid={`edit-repair-${it.repair_id}`}
                                title="Editar"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {it.status !== "completed" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => handleComplete(it.repair_id)}
                                  data-testid={`complete-repair-${it.repair_id}`}
                                  title="Completar"
                                >
                                  <CheckCircle2 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(it.repair_id)}
                                data-testid={`delete-repair-${it.repair_id}`}
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          );
        })}
          </div>
        )}
      </div>

      <AddRepairDialog
        initial={editTarget}
        open={editOpen}
        onOpenChange={(o) => { setEditOpen(o); if (!o) setEditTarget(null); }}
        onSaved={() => { setEditOpen(false); setEditTarget(null); load(); }}
      />

      <AddRepairDialog
        open={newOpen}
        onOpenChange={(o) => { setNewOpen(o); if (!o) setNewDate(null); }}
        initialStartDate={newDate}
        onCreated={() => { setNewOpen(false); setNewDate(null); load(); }}
      />
    </div>
  );
}
